import { createContext, useCallback, useContext, useState, type ReactNode } from "react";

/**
 * Search scope — determines what the overlay searches when opened from a
 * given trigger.
 *
 *   workspace — all projects + files in the workspace. TopBar pill / rail
 *   project   — files inside a specific project (no project results shown)
 *   folder    — files inside a specific folder (no project results shown)
 */
export type SearchScope =
  | { kind: "workspace" }
  | { kind: "project"; projectId: string; label?: string }
  | { kind: "folder"; folderId: string; label?: string };

type Ctx = {
  open: boolean;
  scope: SearchScope;
  openWith: (scope: SearchScope) => void;
  close: () => void;
};

const SearchOverlayCtx = createContext<Ctx>({
  open: false,
  scope: { kind: "workspace" },
  openWith: () => {},
  close: () => {},
});

export function SearchOverlayProvider({ children }: { children: ReactNode }) {
  const [open, setOpen] = useState(false);
  const [scope, setScope] = useState<SearchScope>({ kind: "workspace" });

  const openWith = useCallback((s: SearchScope) => {
    setScope(s);
    setOpen(true);
  }, []);
  const close = useCallback(() => setOpen(false), []);

  return (
    <SearchOverlayCtx.Provider value={{ open, scope, openWith, close }}>
      {children}
    </SearchOverlayCtx.Provider>
  );
}

/**
 * Consumer hook — any component under a SearchOverlayProvider can open the
 * overlay with a specific scope. The workspace-wide TopBar and rail pass a
 * workspace scope; in-project / in-folder search buttons pass their own.
 *
 * Mobile callers should still route to /search — the overlay is desktop-only.
 * Use `isDesktopViewport()` to gate.
 */
export function useSearchOverlay() {
  return useContext(SearchOverlayCtx);
}

/** Media-query gate — matches the app's md: breakpoint (768px). */
export function isDesktopViewport(): boolean {
  if (typeof window === "undefined") return true;
  return window.matchMedia("(min-width: 768px)").matches;
}
